import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AnalyticsVisualization } from '../types/analytics.types';
import { useDashboardAiContext } from '../contexts/DashboardAiContext';
import { AgenticFeatureRegistry } from '../../../ai/chat/AgenticFeatureRegistry';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: '600px',
    maxWidth: '800px',
  },
}));

interface PropertyField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'color' | 'select' | 'textarea' | 'boolean';
  defaultValue?: any;
  options?: { value: any; label: string }[];
  helperText?: string;
  section?: string;
}

interface PropertyEditorProps {
  open: boolean;
  visualization: AnalyticsVisualization | null;
  onClose: () => void;
  onSave: (visualizationId: string, updates: Record<string, any>) => void;
}

/**
 * Generic property editor that introspects visualization config types
 * and generates appropriate form fields
 */
export const PropertyEditor: React.FC<PropertyEditorProps> = ({
  open,
  visualization,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [configData, setConfigData] = useState<Record<string, any>>({});
  const [layoutData, setLayoutData] = useState<Record<string, any>>({});
  
  // AI test state
  const [testResult, setTestResult] = useState<{ 
    loading: boolean; 
    response?: string; 
    error?: string; 
    status?: string;
    threadId?: string;
    messageId?: string;
    intelligenceCard?: { headline?: string; byline?: string; fullText?: string };
  } | null>(null);
  const dashboardAi = useDashboardAiContext();

  // Debug: Log testResult changes
  useEffect(() => {
    if (testResult) {
      console.log(`📊 [PropertyEditor] testResult updated:`, {
        loading: testResult.loading,
        status: testResult.status,
        hasResponse: !!testResult.response,
        hasError: !!testResult.error,
        hasIntelligenceCard: !!testResult.intelligenceCard,
      });
    }
  }, [testResult]);

  // Listen for intelligenceCard updates from ChatInterface
  useEffect(() => {
    const handleIntelligenceCardUpdate = (event: CustomEvent) => {
      const { intelligenceCard, threadId, messageId } = event.detail;
      
      // Only update if this is for our current thread
      if (testResult?.threadId && threadId === testResult.threadId) {
        console.log(`📊 [PropertyEditor] Received intelligenceCard update for thread ${threadId}:`, {
          headline: intelligenceCard.headline,
          byline: intelligenceCard.byline,
          hasFullText: !!intelligenceCard.fullText,
        });
        
        setTestResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            intelligenceCard: {
              ...prev.intelligenceCard,
              ...intelligenceCard, // Merge new intelligenceCard data
            },
          };
        });
      }
    };
    
    window.addEventListener('intelligenceCardUpdate', handleIntelligenceCardUpdate as EventListener);
    
    return () => {
      window.removeEventListener('intelligenceCardUpdate', handleIntelligenceCardUpdate as EventListener);
    };
  }, [testResult?.threadId]);

  // Generate property fields based on visualization type
  const getPropertyFields = (): PropertyField[] => {
    if (!visualization) return [];

    const fields: PropertyField[] = [];

    // Common layout properties
    fields.push(
      {
        key: 'layout.order',
        label: 'Order',
        type: 'number',
        helperText: 'Display order (lower numbers appear first)',
        section: 'Layout',
      },
      {
        key: 'layout.span',
        label: 'Span',
        type: 'number',
        helperText: 'Column width (1-12, where 12 is full width)',
        section: 'Layout',
      }
    );

    // Type-specific properties
    switch (visualization.type) {
      case 'ai-insight':
        fields.push(
          {
            key: 'config.prompt',
            label: 'AI Prompt',
            type: 'textarea',
            helperText: 'The prompt to send to the AI layer for generating insights',
            section: 'AI Configuration',
          },
          {
            key: 'config.backgroundColor',
            label: 'Background Color',
            type: 'color',
            helperText: 'Card background color (e.g., #000000 for black)',
            section: 'Styling',
          },
          {
            key: 'config.accentColor',
            label: 'Accent Color',
            type: 'color',
            helperText: 'Border and highlight color',
            section: 'Styling',
          },
          {
            key: 'config.variant',
            label: 'Variant',
            type: 'select',
            options: [
              { value: 'compact', label: 'Compact' },
              { value: 'standard', label: 'Standard' },
              { value: 'featured', label: 'Featured' },
            ],
            section: 'Styling',
          },
          {
            key: 'config.icon',
            label: 'Icon',
            type: 'select',
            options: [
              { value: 'sparkles', label: 'Sparkles' },
              { value: 'lightbulb', label: 'Lightbulb' },
              { value: 'chart', label: 'Chart' },
              { value: 'trending', label: 'Trending' },
            ],
            section: 'Styling',
          },
          {
            key: 'config.showLearnMore',
            label: 'Show Learn More',
            type: 'boolean',
            section: 'Options',
          },
          {
            key: 'config.learnMoreText',
            label: 'Learn More Text',
            type: 'text',
            section: 'Options',
          }
        );
        break;

      case 'kpi':
      case 'performance-card':
        fields.push(
          {
            key: 'config.backgroundColor',
            label: 'Background Color',
            type: 'color',
            section: 'Styling',
          },
          {
            key: 'config.accentColor',
            label: 'Accent Color',
            type: 'color',
            section: 'Styling',
          }
        );
        break;

      case 'chart':
        fields.push(
          {
            key: 'config.title',
            label: 'Chart Title',
            type: 'text',
            section: 'Display',
          },
          {
            key: 'config.backgroundColor',
            label: 'Background Color',
            type: 'color',
            section: 'Styling',
          }
        );
        break;

      case 'table':
        fields.push(
          {
            key: 'config.title',
            label: 'Table Title',
            type: 'text',
            section: 'Display',
          },
          {
            key: 'config.backgroundColor',
            label: 'Background Color',
            type: 'color',
            section: 'Styling',
          }
        );
        break;
    }

    return fields;
  };

  // Initialize form data from visualization
  useEffect(() => {
    if (!visualization) return;

    const initialFormData: Record<string, any> = {};
    const initialConfigData: Record<string, any> = { ...(visualization.config || {}) };
    const initialLayoutData: Record<string, any> = { ...(visualization.layout || {}) };

    // Set initial values for all fields
    getPropertyFields().forEach(field => {
      if (field.key.startsWith('config.')) {
        const configKey = field.key.replace('config.', '');
        initialFormData[field.key] = initialConfigData[configKey] ?? field.defaultValue ?? '';
      } else if (field.key.startsWith('layout.')) {
        const layoutKey = field.key.replace('layout.', '');
        initialFormData[field.key] = initialLayoutData[layoutKey] ?? field.defaultValue ?? '';
      }
    });

    setFormData(initialFormData);
    setConfigData(initialConfigData);
    setLayoutData(initialLayoutData);
    // Clear test result when visualization changes
    setTestResult(null);
  }, [visualization]);

  const handleFieldChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleSave = () => {
    if (!visualization) return;

    const configUpdates: Record<string, any> = {};
    const layoutUpdates: Record<string, any> = {};

    // Separate config and layout updates
    Object.keys(formData).forEach(key => {
      if (key.startsWith('config.')) {
        const configKey = key.replace('config.', '');
        configUpdates[configKey] = formData[key];
      } else if (key.startsWith('layout.')) {
        const layoutKey = key.replace('layout.', '');
        layoutUpdates[layoutKey] = formData[key];
      }
    });

    // Merge updates
    const updates: Record<string, any> = {};
    if (Object.keys(configUpdates).length > 0) {
      updates.config = { ...configData, ...configUpdates };
    }
    if (Object.keys(layoutUpdates).length > 0) {
      updates.layout = { ...layoutData, ...layoutUpdates };
    }

    onSave(visualization.id, updates);
    onClose();
    // Clear test result on save
    setTestResult(null);
  };
  
  // Clear test result when dialog closes
  useEffect(() => {
    if (!open) {
      setTestResult(null);
    }
  }, [open]);

  const renderField = (field: PropertyField) => {
    const value = formData[field.key] ?? field.defaultValue ?? '';

    switch (field.type) {
      case 'textarea':
        // Special handling for prompt field - add test button
        if (field.key === 'config.prompt') {
          return (
            <Box key={field.key}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={field.label}
                value={value}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                helperText={field.helperText}
                variant="outlined"
                size="small"
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                {dashboardAi ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {dashboardAi.isLoading && (
                      <CircularProgress size={16} />
                    )}
                    <Typography variant="caption" color={dashboardAi.isReady ? 'success.main' : 'text.secondary'}>
                      {dashboardAi.isLoading 
                        ? 'Initializing AI...' 
                        : dashboardAi.isReady 
                          ? `AI Ready (App: ${dashboardAi.applicationKey || 'N/A'})`
                          : dashboardAi.error 
                            ? `AI Error: ${dashboardAi.error}`
                            : 'AI Not Ready'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="warning.main">
                    AI context not available. Dashboard needs a derived application.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={async () => {
                    if (!value) {
                      setTestResult({ loading: false, error: 'Please enter a prompt to test.' });
                      return;
                    }
                    
                    if (!dashboardAi?.executeInsightPrompt) {
                      setTestResult({ loading: false, error: 'AI context not available. Make sure the dashboard has a derived application configured and the AI assistant is initialized.' });
                      return;
                    }
                    
                    if (!dashboardAi.isReady) {
                      setTestResult({ loading: false, error: `AI assistant not ready. ${dashboardAi.error || 'Please wait for initialization to complete.'}` });
                      return;
                    }
                    
                    setTestResult({ loading: true, status: 'Starting...' });
                    try {
                      // Configure AgenticFeatureRegistry to use application context (not feature context)
                      // This ensures we use the general AI agent, not the dashboard-building agent
                      const currentSettings = AgenticFeatureRegistry.getSettings();
                      const originalSettings = { ...currentSettings };
                      
                      if (dashboardAi?.applicationKey) {
                        // Set application context and clear feature key
                        AgenticFeatureRegistry.setApplicationContext(
                          dashboardAi.applicationKey,
                          '', // assistantKey - empty for default
                          dashboardAi.applicationKey // agentId
                        );
                        console.log(`🔧 [PropertyEditor] Configured AI context: applicationKey=${dashboardAi.applicationKey}, cleared featureKey`);
                      }
                      
                      const response = await dashboardAi.executeInsightPrompt(value, (status) => {
                        console.log(`🔄 [PropertyEditor] Status update: ${status}`);
                        setTestResult(prev => {
                          // Always preserve loading state during status updates (until Complete)
                          const isLoading = status !== 'Complete' && status !== 'Error';
                          if (!prev) {
                            return { loading: isLoading, status };
                          }
                          return { 
                            ...prev, 
                            status, 
                            loading: isLoading,
                            // Clear error if we're making progress
                            error: isLoading ? undefined : prev.error
                          };
                        });
                      });
                      
                      // Log the intelligence card fields for debugging
                      console.log(`📊 [PropertyEditor] Received intelligenceCard:`, {
                        headline: response.intelligenceCard?.headline,
                        byline: response.intelligenceCard?.byline,
                        fullText: response.intelligenceCard?.fullText ? 'present' : 'missing',
                        hasHeadline: !!response.intelligenceCard?.headline,
                        hasByline: !!response.intelligenceCard?.byline,
                        intelligenceCardObject: response.intelligenceCard,
                      });
                      
                      setTestResult({ 
                        loading: false, 
                        response: response.markdown || response.text,
                        status: 'Complete',
                        threadId: response.threadId,
                        messageId: response.messageId,
                        intelligenceCard: response.intelligenceCard,
                      });
                      
                      // Open ChatInterface to view the conversation
                      if (response.threadId && dashboardAi?.applicationKey) {
                        AgenticFeatureRegistry.setApplicationContext(
                          dashboardAi.applicationKey,
                          '',
                          dashboardAi.applicationKey
                        );
                        AgenticFeatureRegistry.setFocusKey(response.threadId);
                        window.dispatchEvent(new CustomEvent('openAIChat', { 
                          detail: { threadId: response.threadId } 
                        }));
                        console.log(`🎯 [PropertyEditor] Opening ChatInterface for thread: ${response.threadId}`);
                      }
                      
                      // Restore original settings (optional - might want to keep the context for chat interface)
                      // AgenticFeatureRegistry.updateSettings(originalSettings);
                    } catch (err) {
                      setTestResult({ 
                        loading: false, 
                        error: err instanceof Error ? err.message : 'Failed to execute prompt',
                        status: 'Error'
                      });
                    }
                  }}
                  disabled={!value || testResult?.loading || !dashboardAi?.isReady}
                >
                  {testResult?.loading ? 'Testing...' : 'Test Prompt'}
                </Button>
              </Box>
              {testResult && (
                <Box sx={{ mt: 2 }}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="primary">
                        AI Test Session
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {testResult.threadId && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              // Open full chat drawer
                              if (testResult?.threadId && dashboardAi?.applicationKey) {
                                AgenticFeatureRegistry.setApplicationContext(
                                  dashboardAi.applicationKey,
                                  '',
                                  dashboardAi.applicationKey
                                );
                                AgenticFeatureRegistry.setFocusKey(testResult.threadId);
                                window.dispatchEvent(new CustomEvent('openAIChat', { 
                                  detail: { threadId: testResult.threadId } 
                                }));
                              }
                            }}
                            startIcon={<OpenInNewIcon />}
                          >
                            Open Full Chat
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => setTestResult(null)}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Display Headline and Byline if extracted */}
                    {testResult.intelligenceCard && (testResult.intelligenceCard.headline || testResult.intelligenceCard.byline) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                          Extracted Intelligence Card Fields
                        </Typography>
                        <Box sx={{ 
                          p: 1.5, 
                          backgroundColor: 'background.default',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}>
                          {testResult.intelligenceCard.headline && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Headline:
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {testResult.intelligenceCard.headline}
                              </Typography>
                            </Box>
                          )}
                          {testResult.intelligenceCard.byline && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Byline:
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {testResult.intelligenceCard.byline}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Note: Conversation is displayed in ChatInterface (opened via "Open Full Chat" button) */}
                    {testResult.threadId && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.contrastText">
                          💬 View the full conversation in the Chat Interface (click "Open Full Chat" above)
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          );
        }
        
        return (
          <TextField
            key={field.key}
            fullWidth
            multiline
            rows={4}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            helperText={field.helperText}
            variant="outlined"
            size="small"
          />
        );

      case 'color':
        return (
          <Box key={field.key}>
            <TextField
              fullWidth
              label={field.label}
              value={value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              helperText={field.helperText}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      backgroundColor: value || '#ffffff',
                      border: '1px solid #ccc',
                      mr: 1,
                    }}
                  />
                ),
              }}
            />
          </Box>
        );

      case 'select':
        return (
          <FormControl key={field.key} fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              label={field.label}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.helperText && <FormHelperText>{field.helperText}</FormHelperText>}
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControl key={field.key} fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value ? 'true' : 'false'}
              onChange={(e) => handleFieldChange(field.key, e.target.value === 'true')}
              label={field.label}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
            {field.helperText && <FormHelperText>{field.helperText}</FormHelperText>}
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            key={field.key}
            fullWidth
            type="number"
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : '')}
            helperText={field.helperText}
            variant="outlined"
            size="small"
          />
        );

      default:
        return (
          <TextField
            key={field.key}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            helperText={field.helperText}
            variant="outlined"
            size="small"
          />
        );
    }
  };

  const fields = getPropertyFields();
  const sections = Array.from(new Set(fields.map(f => f.section || 'General')));

  if (!visualization) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography variant="h6">Edit Properties: {visualization.title || visualization.id}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {sections.map((section, sectionIndex) => {
            const sectionFields = fields.filter(f => (f.section || 'General') === section);
            if (sectionFields.length === 0) return null;

            return (
              <Box key={section} sx={{ mb: sectionIndex < sections.length - 1 ? 3 : 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                  {section}
                </Typography>
                <Grid container spacing={2}>
                  {sectionFields.map(field => (
                    <Grid item xs={12} key={field.key}>
                      {renderField(field)}
                    </Grid>
                  ))}
                </Grid>
                {sectionIndex < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
